import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { CTABanner } from '../components/CTABanner';
import { ServiceFAQ } from '../components/ServiceFAQ';
import { Check, ArrowUpRight } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/satyam-thriveitmedia/discovery-call';

const whatWeDo = [
  'Influencer sourcing and vetting (nano: 1K-10K, micro: 10K-100K)',
  'Campaign strategy and creative brief development',
  'Creator outreach, coordination, and briefing',
  'UGC-style content production for ads and organic',
  'Campaign performance tracking and reporting',
];

const whoFor = [
  'D2C brands selling on Instagram, Flipkart, Amazon, or their own website',
  'Ecommerce brands looking to scale through creator content',
  'Brands spending on Meta ads who want cheaper, more authentic creative',
];

const whyUs = [
  '1500+ creator collaborations completed',
  'Deep network of nano and micro influencers globally',
  'Focus on ROI, not just reach',
  'Based in Bengaluru, working with brands globally',
];

const faqs = [
  { question: 'What is nano influencer marketing?', answer: 'Nano influencer marketing involves partnering with creators who have between 1,000 and 10,000 followers. These creators have highly engaged, niche audiences and often deliver better conversion rates than larger influencers because their followers trust their recommendations as authentic and relatable.' },
  { question: 'How much does influencer marketing cost?', answer: 'Influencer marketing costs vary significantly based on creator size, platform, and campaign scope. Nano influencer campaigns can start from a few hundred dollars per month through barter or paid deals. Micro influencer campaigns typically range higher depending on the number of creators and deliverables. We recommend starting with a small test campaign and scaling based on results.' },
  { question: 'How do you measure influencer campaign results?', answer: 'We track multiple metrics including reach, engagement rate, click-through rates, conversions, and ROAS. Every campaign comes with detailed performance reports so you can see exactly what is working and where to optimize.' },
  { question: 'Do you work with both Instagram and LinkedIn influencers?', answer: 'Yes. For D2C brands, we primarily work with Instagram creators. For employer branding campaigns, we also work with LinkedIn creators and thought leaders to help you reach professional audiences.' },
  { question: 'How is nano influencer marketing different from celebrity marketing?', answer: 'Celebrity marketing relies on massive reach but often has low engagement and high costs. Nano influencer marketing focuses on trust and relevance — smaller creators have closer relationships with their followers, which leads to higher engagement rates and better conversion for D2C brands.' },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Influencer Marketing for D2C Brands",
  "provider": { "@type": "Organization", "name": "Thriveit Media", "url": "https://thriveitmedia.in" },
  "description": "Nano and micro influencer campaigns for D2C and ecommerce brands that drive sales, not just reach.",
  "areaServed": "Worldwide"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": { "@type": "Answer", "text": f.answer }
  }))
};

export default function InfluencerMarketingPage() {
  return (
    <>
      <SEOHead
        title="Influencer Marketing Agency for D2C Brands | Thriveit Media"
        description="Thriveit Media runs nano and micro influencer campaigns for D2C brands that drive sales, not just reach. 1500+ creator collaborations managed."
        canonical="https://thriveitmedia.in/services/influencer-marketing"
        schema={[serviceSchema, faqSchema]}
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">Influencer Marketing</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-5xl mb-8">
              Influencer Marketing Agency for D2C Brands
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Why It Fails */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <ScrollReveal>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-8">Why Most Influencer Campaigns Fail for D2C Brands</h2>
              <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed">
                The biggest mistake D2C brands make with influencer marketing is optimising for reach instead of relevance. A creator with 500,000 followers sounds impressive but rarely drives as many purchases as five creators with 10,000 highly engaged followers each. Large creators have diluted audiences with low purchase intent. Nano and micro influencers have communities that genuinely trust their recommendations. Creator-audience fit matters more than follower count in driving conversions. As an influencer marketing agency for D2C brands, we do it differently — every creator is vetted for engagement quality, audience demographics, and category relevance.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <div className="bg-[#0a0a0a] border border-white/5 p-10">
                <div className="space-y-6">
                  <div className="flex justify-between border-b border-white/5 pb-4">
                    <span className="text-sm font-body text-[#888880]">Avg. Engagement Rate</span>
                    <span className="text-2xl font-heading font-extrabold text-[#FF5C35]">8.2%</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-4">
                    <span className="text-sm font-body text-[#888880]">Creator Collaborations</span>
                    <span className="text-2xl font-heading font-extrabold text-[#F5F5F0]">1500+</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-body text-[#888880]">ROAS Delivered</span>
                    <span className="text-2xl font-heading font-extrabold text-[#F5F5F0]">5x</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">What We Do</h2>
          </ScrollReveal>
          <StaggerContainer className="space-y-4">
            {whatWeDo.map((item) => (
              <StaggerItem key={item} className="flex items-start gap-4 bg-[#111111] border border-white/5 p-6">
                <Check className="w-5 h-5 text-[#FF5C35] mt-0.5 flex-shrink-0" />
                <p className="text-base font-body font-light text-[#F5F5F0]">{item}</p>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">Who This Is For</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whoFor.map((item, i) => (
              <StaggerItem key={i}>
                <div className="bg-[#0a0a0a] border border-white/5 p-8 h-full">
                  <span className="text-4xl font-heading font-extrabold text-white/5">{String(i + 1).padStart(2, '0')}</span>
                  <p className="text-base font-body font-light text-[#888880] mt-4 leading-relaxed">{item}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">Why Choose Thriveit Media for Influencer Marketing?</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyUs.map((item, i) => (
              <StaggerItem key={i} className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#FF5C35] mt-2 flex-shrink-0" />
                <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed">{item}</p>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <CTABanner
        heading="Book a Free Influencer Marketing Consultation"
        subtext="Let's discuss how nano and micro influencer campaigns can drive real sales for your D2C brand."
        buttonText="Book a Free Consultation"
      />
    </>
  );
}
